// Suivi de caisse (livre de caisse) : alimentations (entrées / débit) et dépenses (sorties / crédit),
// présentées par mois avec solde reporté, solde courant ligne par ligne et solde final. Registre indépendant des charges.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  AddOutlined, ChevronLeft, ChevronRight, DeleteOutline, EditOutlined, FileDownloadOutlined, SearchOutlined,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { caisseAPI } from '../../services/api';
import { apiError, today } from '../../utils/format';
import { ecrireExcel } from '../../utils/excel';
import { tokens } from '../../theme';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const libMois = (m) => `${MOIS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const moisDe = (d) => d.slice(0, 7);
const decale = (m, n) => { const d = new Date(`${m}-01T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7); };
const F = (n) => (n === null || n === undefined ? '—' : `${Math.round(n).toLocaleString('fr-FR')} F`);
const frDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const num = (v) => Number(String(v ?? '').replace(',', '.'));

function MouvementDialog({ open, mouvement, moisDefaut, onClose, onDone }) {
  const [f, setF] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError('');
    if (mouvement) {
      setF({ ...mouvement, type: mouvement.alimentation > 0 ? 'ALIMENTATION' : 'DEPENSE', montant: (mouvement.alimentation || mouvement.depense) || '' });
    } else {
      setF({ type: 'DEPENSE', date_mouvement: (moisDefaut && `${moisDefaut}-01` > today()) ? `${moisDefaut}-01` : today(), libelle: '', quantite: '', prix_unitaire: '', montant: '', observation: '' });
    }
  }, [open, mouvement, moisDefaut]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const auto = f.montant === '' || f.montant === null || f.montant === undefined;
  const montantCalc = auto && f.quantite && f.prix_unitaire ? Math.round(num(f.quantite) * num(f.prix_unitaire)) : null;
  const save = async () => {
    setBusy(true); setError('');
    try {
      const payload = { ...f };
      if (mouvement) await caisseAPI.update(mouvement.id, payload); else await caisseAPI.create(payload);
      onDone(mouvement ? 'Mouvement modifié' : 'Mouvement enregistré');
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  if (!open) return null;
  const entree = f.type === 'ALIMENTATION';
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mouvement ? 'Modifier le mouvement' : 'Nouveau mouvement de caisse'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <ToggleButtonGroup exclusive fullWidth color={entree ? 'success' : 'error'} value={f.type || 'DEPENSE'}
          onChange={(e, v) => v && setF((x) => ({ ...x, type: v }))} sx={{ mb: 2 }}>
          <ToggleButton value="ALIMENTATION">Alimentation (entrée)</ToggleButton>
          <ToggleButton value="DEPENSE">Dépense (sortie)</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField type="date" label="Date *" value={f.date_mouvement || ''} onChange={set('date_mouvement')} InputLabelProps={{ shrink: true }} />
          <TextField label="Libellé *" value={f.libelle || ''} onChange={set('libelle')} inputProps={{ maxLength: 255 }} />
          <TextField label="Quantité" value={f.quantite ?? ''} onChange={set('quantite')} inputProps={{ inputMode: 'decimal' }} />
          <TextField label="Prix unitaire (F)" value={f.prix_unitaire ?? ''} onChange={set('prix_unitaire')} inputProps={{ inputMode: 'decimal' }} />
          <TextField label={entree ? 'Montant alimentation (F) *' : 'Montant dépense (F) *'} value={f.montant ?? ''} onChange={set('montant')}
            inputProps={{ inputMode: 'decimal' }} placeholder={montantCalc ? String(montantCalc) : ''}
            helperText={montantCalc ? `Calculé : ${F(montantCalc)} (modifiable)` : 'Sinon quantité × prix unitaire'} />
          <TextField label="Observation" value={f.observation || ''} onChange={set('observation')} inputProps={{ maxLength: 255 }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={busy || !f.libelle || !f.date_mouvement || (!f.montant && !montantCalc)} onClick={save}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
}

const SuiviCaisse = () => {
  const [mois, setMois] = useState(moisDe(today()));
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [resume, setResume] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [dlg, setDlg] = useState(null);

  const charger = useCallback(() => {
    caisseAPI.list({ mois, q: q || undefined }).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
    caisseAPI.resume().then((r) => setResume(r.data)).catch(() => {});
  }, [mois, q]);
  useEffect(() => { const t = setTimeout(charger, 200); return () => clearTimeout(t); }, [charger]);

  const moisAvecDonnees = useMemo(() => (resume?.mois || []).some((m) => m.nombre), [resume]);
  const supprimer = async (m) => {
    if (!window.confirm(`Supprimer le mouvement « ${m.libelle} » ?`)) return;
    try { await caisseAPI.supprimer(m.id); setInfo('Mouvement supprimé'); charger(); } catch (e) { setError(apiError(e)); }
  };
  const exporter = async () => {
    const lignes = [
      [{ value: `Suivi de caisse — ${libMois(mois)}`, fontWeight: 'bold' }],
      ['DATE', 'LIBELLE', 'QUANTITE', 'PRIX UNITAIRE', 'ALIMENTATION (DEBIT)', 'DEPENSE (CREDIT)', 'SOLDE'],
      ['', 'Solde reporté', '', '', '', '', Math.round(data.solde_reporte)],
      ...data.mouvements.map((m) => [m.date_mouvement ? new Date(`${m.date_mouvement}T00:00:00Z`) : null, m.libelle,
        m.quantite ?? '', m.prix_unitaire ? Math.round(m.prix_unitaire) : '', m.alimentation ? Math.round(m.alimentation) : '',
        m.depense ? Math.round(m.depense) : '', Math.round(m.solde)]),
      [], ['', 'TOTAUX', '', '', Math.round(data.total_alimentation), Math.round(data.total_depense), ''],
      ['', 'SOLDE FINAL', '', '', '', '', Math.round(data.solde_final)],
    ];
    await ecrireExcel(lignes, `suivi-caisse-${mois}.xlsx`, { largeurs: [12, 40, 10, 14, 20, 18, 16] });
  };

  return (
    <Layout title="Suivi de caisse" plain
      actions={<>
        <Button startIcon={<FileDownloadOutlined />} disabled={!data?.mouvements.length} onClick={exporter}>Excel</Button>
        <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setDlg({})}>Nouveau mouvement</Button>
      </>}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => setMois(decale(mois, -1))}><ChevronLeft /></IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: 18, minWidth: 150, textAlign: 'center' }}>{libMois(mois)}</Typography>
        <IconButton onClick={() => setMois(decale(mois, 1))} disabled={mois >= moisDe(today())}><ChevronRight /></IconButton>
        <TextField size="small" placeholder="Rechercher (libellé, observation)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 260, flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <Kpi compact tint="navy" label="Solde de caisse actuel" value={F(resume?.solde_global)} valueSize={22} sub="toutes périodes" />
        <Kpi compact tint="gray" label="Solde reporté" value={F(data?.solde_reporte)} valueSize={22} sub={`début ${libMois(mois)}`} />
        <Kpi compact tint="green" label={`Alimentations — ${libMois(mois)}`} value={F(data?.total_alimentation)} valueSize={22} sub="entrées" />
        <Kpi compact tint="amber" label={`Dépenses — ${libMois(mois)}`} value={F(data?.total_depense)} valueSize={22} sub="sorties" />
      </Box>

      <Panel flush>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Libellé</TableCell>
                <TableCell align="right">Quantité</TableCell><TableCell align="right">Prix unitaire</TableCell>
                <TableCell align="right">Alimentation (débit)</TableCell><TableCell align="right">Dépense (crédit)</TableCell>
                <TableCell align="right">Solde</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ '& td': { color: 'text.secondary', fontStyle: 'italic' } }}>
                <TableCell colSpan={6}>Solde reporté (début {libMois(mois)})</TableCell>
                <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{F(data?.solde_reporte)}</TableCell><TableCell />
              </TableRow>
              {data?.mouvements.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{frDate(m.date_mouvement)}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14 }}>{m.libelle}</Typography>
                    {m.observation && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{m.observation}</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>{m.quantite ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{m.prix_unitaire ? F(m.prix_unitaire) : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'success.main', whiteSpace: 'nowrap' }}>{m.alimentation ? F(m.alimentation) : ''}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'error.main', whiteSpace: 'nowrap' }}>{m.depense ? F(m.depense) : ''}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{F(m.solde)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Modifier"><IconButton size="small" onClick={() => setDlg({ mouvement: m })}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Supprimer"><IconButton size="small" onClick={() => supprimer(m)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {data && !data.mouvements.length && <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucun mouvement pour {libMois(mois)}.{moisAvecDonnees ? ' D’autres mois contiennent des mouvements (flèches ci-dessus).' : ' Enregistrez une alimentation ou une dépense.'}</TableCell></TableRow>}
              {data && data.mouvements.length > 0 && (
                <>
                  <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 } }}>
                    <TableCell colSpan={4}>TOTAUX — {libMois(mois)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'success.main' }}>{F(data.total_alimentation)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, color: 'error.main' }}>{F(data.total_depense)}</TableCell>
                    <TableCell /><TableCell />
                  </TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                    <TableCell colSpan={6}>SOLDE FINAL — {libMois(mois)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{F(data.solde_final)}</TableCell><TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <MouvementDialog open={!!dlg} mouvement={dlg?.mouvement} moisDefaut={mois} onClose={() => setDlg(null)} onDone={(m) => { setDlg(null); setInfo(m); charger(); }} />
    </Layout>
  );
};

export default SuiviCaisse;
